<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/mixins";

  .add-teammate-view {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;

    .header {
      align-items: center;
      border-bottom: utils.in_rem(1px) solid
        colors.get(interfaceTransparentWhite_15);
      display: flex;
      gap: utils.in_rem(12px);
      padding: utils.in_rem(12px);

      .back-button {
        background: none;
        border: none;
        color: colors.get(textWhite);
        cursor: pointer;
        font-size: utils.in_rem(16px);
        padding: utils.in_rem(4px);
        transition: opacity 0.15s ease;

        &:hover {
          opacity: 0.8;
        }
      }

      .title {
        color: colors.get(textWhite);
        flex: 1;
        margin: 0;
        @include mixins.body2;
        font-weight: 500;
      }
    }

    .search-container {
      border-bottom: utils.in_rem(1px) solid
        colors.get(interfaceTransparentWhite_15);
      padding: utils.in_rem(12px);

      .search-input {
        background-color: colors.get(interfaceTransparentWhite_15);
        border: utils.in_rem(1px) solid colors.get(interfaceTransparentWhite_30);
        border-radius: utils.in_rem(8px);
        color: colors.get(textWhite);
        padding: utils.in_rem(8px) utils.in_rem(12px);
        width: 100%;
        @include mixins.body3;

        &::placeholder {
          color: colors.get(interfaceTransparentWhite_70);
        }

        &:focus {
          background-color: colors.get(interfaceTransparentWhite_30);
          border-color: colors.get(primary_50);
          outline: none;
        }
      }
    }

    .teammates-list {
      flex: 1;
      overflow-y: auto;
      padding-bottom: utils.in_rem(12px);

      &::-webkit-scrollbar {
        width: utils.in_rem(6px);
      }

      &::-webkit-scrollbar-track {
        background: transparent;
      }

      &::-webkit-scrollbar-thumb {
        background: colors.get(interfaceTransparentWhite_30);
        border-radius: utils.in_rem(3px);
      }
    }
  }
</style>

<script>
  import Icon from "../../../../../../svelte-design-system/components/atoms/Icon.svelte";
  import { usersStore } from "../../../../api-stores/user-store";
  import { participants } from "../../../../api/dialler";
  import TeammateRow from "../components/TeammateRow.svelte";
  import { diallerState } from "../diallerState.svelte";

  let searchQuery = $state("");
  const teammates = $derived($usersStore);

  // Group teammates by first letter of last name, filtered by search
  const groupedTeammates = $derived.by(() => {
    // Filter first
    const query = searchQuery?.toLowerCase();
    const filtered = query
      ? teammates.filter((teammate) => {
          const fullName =
            `${teammate.firstName || ""} ${teammate.lastName || ""}`.toLowerCase();
          const phone = (teammate.phone || "").toLowerCase();
          return fullName.includes(query) || phone.includes(query);
        })
      : teammates;

    // Group by letter
    const groups = {};
    filtered.forEach((teammate) => {
      const letter = (
        teammate.lastName?.[0] ||
        teammate.firstName?.[0] ||
        "#"
      ).toUpperCase();
      if (!groups[letter]) {
        groups[letter] = [];
      }
      groups[letter].push(teammate);
    });

    // Sort by letter and return
    return Object.keys(groups)
      .sort()
      .map((letter) => ({
        letter,
        teammates: groups[letter].sort((a, b) => {
          const aName = a.lastName?.toLowerCase() || "#";
          const bName = b.lastName?.toLowerCase() || "#";
          return aName.localeCompare(bName);
        }),
      }));
  });

  const handleBack = () => {
    diallerState.viewMode = "user";
  };

  const handleTeammateSelect = async (teammate) => {
    // Add the pending teammate to the call
    diallerState.addTeammate({
      ...teammate,
      inviteStatus: "pending",
      role: "teammate",
    });

    // Invite the teammate to the call
    await participants({
      action: "invite",
      callId: diallerState.callId,
      inviteStatus: "pending",
      invitedUserId: teammate.userId,
    });
  };

  const handleSearchInput = (e) => {
    searchQuery = e.target.value;
  };
</script>

<div class="add-teammate-view">
  <div class="header">
    <button
      class="back-button"
      onclick={handleBack}
      aria-label="Go back"
      type="button"
    >
      <Icon class="fa-solid fa-arrow-left" size="small" />
    </button>
    <h2 class="title">Add teammate to call</h2>
  </div>

  <div class="search-container">
    <input
      class="search-input"
      type="text"
      placeholder="Search teammates name..."
      value={searchQuery}
      oninput={handleSearchInput}
      aria-label="Search teammates"
    />
  </div>

  <div class="teammates-list">
    {#each groupedTeammates as group}
      {#each group.teammates as teammate, index}
        <TeammateRow
          {teammate}
          showSectionHeader={index === 0}
          sectionLetter={group.letter}
          onclick={() => handleTeammateSelect(teammate)}
        />
      {/each}
    {/each}
  </div>
</div>
